import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Icon, SectionHeading, Tooltip } from '@ets/design-system';
import { useParams } from 'react-router';

import { giveGroupPermission, removeGroupPermission } from 'App/redux/appSlice';
import { useDispatch, usePublicGroup } from '_common/hooks';
import { SearchUser } from '_common/components';
import type { UserOption } from '_common/components/SearchUser/SearchUser';

import Table, { type TableProps } from '../Table/Table';
import styles from '../GroupSettingsPage.module.scss';

const AdminsView = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { id: groupId }: { id: ObjectId } = useParams();

  const { group } = usePublicGroup(groupId);

  const usersList = useMemo(
    () => [
      group.owner,
      ...Object.keys(group.permissions.users).filter((user) =>
        group.permissions.users[user].includes('owner'),
      ),
    ],
    [group],
  );

  const [adminSelected, setAdminSelected] = useState<UserOption | null>(null);
  const userIsOwner =
    group.user_permissions.includes('admin') || group.user_permissions.includes('owner');

  const handleOnClickAddAdmin = () => {
    if (adminSelected) {
      dispatch(giveGroupPermission({ groupId: group.id, userId: adminSelected.value }));
      setAdminSelected(null);
    }
  };

  const handleRemoveUser: TableProps['removeUserHandler'] = (user) => {
    dispatch(removeGroupPermission({ groupId: group.id, userId: user.id }));
  };

  return (
    <>
      {/* Admins */}
      <SectionHeading
        testId="groups-admins"
        size="l"
        title={
          <div className={styles.titleContainer}>
            <span>
              {intl.formatMessage({
                id: 'settings.group.groupAdministratorsTitle',
              })}
            </span>
            <Tooltip
              content={intl.formatMessage({ id: 'settings.group.groupAdministratorsSubtitle' })}
              testId="groups-admins-info"
            >
              <span>
                <Icon icon="circle-info" size="2" testId="info" style={{ marginLeft: '1rem' }} />
              </span>
            </Tooltip>
          </div>
        }
        firstAction={
          userIsOwner
            ? {
                children: intl.formatMessage({ id: 'global.add' }),
                variant: 'primary',
                onClick: handleOnClickAddAdmin,
                disabled: !adminSelected,
              }
            : undefined
        }
        searchBar={
          userIsOwner ? (
            <SearchUser
              value={adminSelected}
              valuesToFilter={[group.owner, ...Object.keys(group.permissions.users)]}
              onChange={(user) => setAdminSelected(user as UserOption | null)}
              placeholder={intl.formatMessage({
                id: 'settings.group.placeholderAutocomplete',
              })}
              escapeClearsValue
              width="37rem"
              testId="admins"
              size="m"
              menuLabel={intl.formatMessage({ id: 'START_TYPING_TO_SEE_THE_LIST_OF_USERS' })}
            />
          ) : null
        }
        style={{ margin: '2rem 0' }}
      />
      <Table
        identity="groups_admins"
        users={usersList}
        group={group}
        removeUserHandler={handleRemoveUser}
        testId="admins-tab"
      />
    </>
  );
};

export default AdminsView;
